import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useNgoPortalQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { Suspense, useState, useCallback } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { Button } from '@worldcoo/ui/components/button';
import { ChevronRightIcon } from 'lucide-react';
import { ExportIcon } from '@worldcoo/ui/custom/export-icon';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import { VerticalTabs } from './-components/vertical-tabs';
import { FormHandleProvider, useFormHandle } from './-components/form-context';
import { EditToggleButton } from './-components/edit-toggle-button';
import { PortalProjectsTab } from './-components/portal-projects-tab';
import { PortalPreviewDialog } from './-components/portal-preview-dialog';
import { EyeLinearIcon, AiPendingHint, getAiLangsForFields } from './-components/language-dropdown';
import type { AiTranslatedMeta } from '@worldcoo/monolith/core';
import { buildPortalUrl } from '@/lib/shared';

const tabValues = ['languages', 'details', 'funding', 'testimonials', 'faq', 'projects'] as const;
type TabValue = (typeof tabValues)[number];

export const Route = createFileRoute('/ngos/portals/$portalId')({
	validateSearch: v.object({
		tab: v.fallback(v.optional(v.picklist(tabValues)), undefined),
		editing: v.fallback(v.optional(v.boolean()), undefined),
		plang: v.fallback(v.optional(v.string()), undefined),
		q: v.fallback(v.optional(v.string()), undefined),
		fundingType: v.fallback(v.optional(v.picklist(['myPortal', 'both'])), undefined),
		status: v.fallback(v.optional(v.picklist(['draft', 'fundraising', 'notFundraising'])), undefined),
		page: v.fallback(v.optional(v.number()), undefined),
	}),
	loader: ({ context, params }) => {
		context.queryClient.ensureQueryData(trpc.ngo.getPortal.queryOptions({ portalId: params.portalId }));
		context.queryClient.ensureQueryData(
			trpc.ngo.getProjects.queryOptions({ query: undefined, fundingType: undefined, status: undefined }),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<Suspense fallback={<PortalDetailSkeleton />}>
			<PortalDetail />
		</Suspense>
	);
}

function PortalDetail() {
	const { portalId } = Route.useParams();
	const portal = useNgoPortalQuery(portalId);

	const shouldBlockSearchChange = useCallback(
		(_current: Record<string, unknown>, next: Record<string, unknown>) => next.tab === 'projects',
		[],
	);

	return (
		<FormHandleProvider shouldBlockSearchChange={shouldBlockSearchChange}>
			<PortalDetailInner portal={portal} portalId={portalId} />
		</FormHandleProvider>
	);
}

function PortalDetailInner(props: { portal: ReturnType<typeof useNgoPortalQuery>; portalId: string }) {
	const { t } = useLingui();
	const { tab, editing } = Route.useSearch();
	const navigate = useNavigate({ from: '/ngos/portals/$portalId' });
	const activeTab: TabValue = tab ?? 'languages';
	const isEditing = editing === true;
	const formHandle = useFormHandle();
	const defaultLang = props.portal.languages[0] ?? 'SPA';

	// Read form state for AI-translation hints on tab labels
	const rawFormValues = isEditing ? formHandle?.current?.getValues() : undefined;
	const activeDefaultLang =
		(typeof rawFormValues?.defaultLanguage === 'string' ? rawFormValues.defaultLanguage : undefined) ?? defaultLang;

	const detailsFields = ['title', 'presentation', 'imageAlt'];
	const aiTranslated =
		rawFormValues?.aiTranslated != null &&
		typeof rawFormValues.aiTranslated === 'object' &&
		!Array.isArray(rawFormValues.aiTranslated)
			? (rawFormValues.aiTranslated as AiTranslatedMeta)
			: undefined;
	const detailsLangs = getAiLangsForFields(detailsFields, aiTranslated);

	const changeLang = (langCode: string) => {
		navigate({
			search: (prev) => ({
				...prev,
				plang: langCode === activeDefaultLang ? undefined : langCode,
				editing: true,
			}),
		});
	};

	const aiHint = (langs: string[]) =>
		langs.length > 0
			? (active: boolean) => (
					<AiPendingHint langs={langs} onLangClick={changeLang} size="sm" variant={active ? 'active' : 'default'} />
				)
			: undefined;

	const tabConfig = [
		{ value: 'languages' as const, label: t`Languages` },
		{ value: 'details' as const, label: t`Portal details`, hint: aiHint(detailsLangs) },
		{ value: 'funding' as const, label: t`Funding Details` },
		{ value: 'testimonials' as const, label: t`Testimonials` },
		{ value: 'faq' as const, label: t`FAQ` },
		{ value: 'projects' as const, label: t`Campaigns` },
	];

	return (
		<>
			{/* Header */}
			<MainSectionHeader
				breadcrumbs={
					<div className="flex items-center gap-1 text-sm">
						<Link to="/ngos/portals" className="text-muted-foreground hover:text-foreground">
							{t`My Portals`}
						</Link>
						<ChevronRightIcon className="text-muted-foreground size-3" />
						<span className="text-primary">{props.portal.title ?? props.portalId}</span>
					</div>
				}
				title={
					<h1 className="text-[24px] leading-[38px] font-medium tracking-[-0.2px]">
						{props.portal.title ?? props.portalId}
					</h1>
				}
				actions={
					<>
						{activeTab !== 'projects' && <PreviewButton />}
						{!isEditing && (
							<Button
								variant="outline-warm"
								size="lg"
								onClick={() => window.open(buildPortalUrl(props.portalId), '_blank')}
								title={t`Open`}
								className="gap-2"
							>
								<ExportIcon className="size-5" />
								{t`Open`}
							</Button>
						)}
						{activeTab !== 'projects' && <EditToggleButton isEditing={isEditing} navigate={navigate} />}
					</>
				}
			/>

			{/* Content area with sidebar tabs */}
			<MainSectionContent className="!flex-row gap-12 !overflow-hidden">
				<VerticalTabs
					tabs={tabConfig}
					activeTab={activeTab}
					onTabChange={(value) =>
						navigate({
							search: (prev) => ({
								...prev,
								tab: value === 'languages' ? undefined : value,
								editing: value === 'projects' ? undefined : prev.editing,
								q: value === 'projects' ? prev.q : undefined,
								fundingType: value === 'projects' ? prev.fundingType : undefined,
								status: value === 'projects' ? prev.status : undefined,
								page: value === 'projects' ? prev.page : undefined,
							}),
						})
					}
				/>

				{/* Tab content */}
				<div className="min-h-0 flex-1 overflow-y-auto pt-4">
					{activeTab === 'projects' ? <PortalProjectsTab /> : <Outlet />}
				</div>
			</MainSectionContent>

			{/* Footer portal target */}
			<div id="portal-footer-portal" />
		</>
	);
}

type SliderItemForm = {
	existingImageUrl?: string;
	image: File | '' | undefined;
	texts: Record<string, { quote: string; authorName: string; imageAlt: string }>;
};

type PortalPreviewFormValues = {
	languages?: string[];
	defaultLanguage?: string;
	title?: Record<string, string>;
	presentation?: Record<string, string>;
	imageAlt?: Record<string, string>;
	themeBaseColor?: string;
	logo?: File | '' | undefined;
	coverImage?: File | '' | undefined;
	sliderItems?: SliderItemForm[];
	faqItems?: Record<string, { question: string; answer: string }[]>;
};

type PreviewSnapshot = {
	formValues: PortalPreviewFormValues | undefined;
};

function PreviewButton() {
	const { t } = useLingui();
	const [showPreview, setShowPreview] = useState(false);
	const [snapshot, setSnapshot] = useState<PreviewSnapshot | null>(null);
	const formHandle = useFormHandle();

	const handleOpen = useCallback(() => {
		setSnapshot({
			formValues: formHandle?.current?.getValues(),
		});
		setShowPreview(true);
	}, [formHandle]);

	const handleClose = useCallback((open: boolean) => {
		setShowPreview(open);
	}, []);

	return (
		<>
			<Button variant="outline-warm" size="lg" onClick={handleOpen} className="gap-2">
				<EyeLinearIcon className="size-5" />
				{t`Preview`}
			</Button>
			{snapshot && <PreviewPortal snapshot={snapshot} open={showPreview} onOpenChange={handleClose} />}
		</>
	);
}

function PreviewPortal(props: { snapshot: PreviewSnapshot; open: boolean; onOpenChange: (open: boolean) => void }) {
	const { portalId } = Route.useParams();
	const { plang } = Route.useSearch();
	const portal = useNgoPortalQuery(portalId);
	const { formValues } = props.snapshot;
	const defaultLang = portal.languages[0] ?? 'SPA';
	const lang = plang ?? defaultLang;

	// Build logo URL from form tri-state: File = new upload, '' = removed, undefined = no change
	const formLogo = formValues?.logo;
	let previewLogoUrl: string | undefined;
	if (formLogo instanceof File) {
		previewLogoUrl = URL.createObjectURL(formLogo);
	} else if (formLogo !== '') {
		previewLogoUrl = portal.logo?.location;
	}

	// Build cover image URL from form tri-state
	const formCoverImage = formValues?.coverImage;
	let previewCoverImageUrl: string | undefined;
	if (formCoverImage instanceof File) {
		previewCoverImageUrl = URL.createObjectURL(formCoverImage);
	} else if (formCoverImage !== '') {
		previewCoverImageUrl = portal.media?.location;
	}

	const activeLanguages = formValues?.languages ?? portal.languages;
	const activeDefaultLang = formValues?.defaultLanguage ?? defaultLang;

	// Build slider items for preview (multi-language, resolved in dialog)
	const previewSliderItems = formValues?.sliderItems?.map((item) => {
		let imageUrl: string | undefined;
		if (item.image instanceof File) {
			imageUrl = URL.createObjectURL(item.image);
		} else if (item.image !== '' && item.existingImageUrl) {
			imageUrl = item.existingImageUrl;
		}
		return { imageUrl, texts: item.texts };
	});

	const previewPortal = {
		title: formValues?.title ?? {},
		presentation: formValues?.presentation ?? {},
		imageAlt: formValues?.imageAlt ?? {},
		logoUrl: previewLogoUrl,
		coverImageUrl: previewCoverImageUrl,
		themeBaseColor: formValues?.themeBaseColor ?? portal.themeBaseColor,
		languages: activeLanguages,
		defaultLanguage: activeDefaultLang,
		sliderItems: previewSliderItems,
		faqItems: formValues?.faqItems ?? {},
	};

	return (
		<PortalPreviewDialog
			open={props.open}
			onOpenChange={props.onOpenChange}
			portal={previewPortal}
			selectedLang={lang}
		/>
	);
}

function PortalDetailSkeleton() {
	return (
		<div className="flex flex-1 flex-col">
			<div className="border-portal-border flex min-h-24 shrink-0 items-center border-b bg-white px-6 py-4">
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<Skeleton className="h-4 w-40" />
					<Skeleton className="h-8 w-56" />
				</div>
				<div className="ml-auto flex shrink-0 items-center gap-2">
					<Skeleton className="size-10 rounded-xl" />
					<Skeleton className="size-10 rounded-xl" />
					<Skeleton className="h-10 w-20 rounded-xl" />
				</div>
			</div>
			<div className="flex flex-1">
				<div className="flex w-60 flex-col gap-2 px-6 pt-10">
					{Array.from({ length: 2 }).map((_, i) => (
						// oxlint-disable-next-line react/no-array-index-key
						<Skeleton key={`tab-skeleton-${i}`} className="h-9 rounded-full" />
					))}
				</div>
				<div className="flex-1 p-6">
					<Skeleton className="h-64 rounded-xl" />
				</div>
			</div>
		</div>
	);
}
